import React from 'react';
import { CheckCircle2, Send } from 'lucide-react';
import { PeerTimelineEvent } from '../types/peerFeedback';
import { formatDateTimeInIST } from '../utils/mappers';

interface ActivityTimelineProps {
  events: PeerTimelineEvent[];
}

const ACTION_META: Record<
  PeerTimelineEvent['action'],
  { label: string; icon: React.ReactNode; color: string }
> = {
  created: {
    label: 'Requested feedback',
    icon: <Send className="w-3.5 h-3.5" />,
    color: 'bg-indigo-100 text-indigo-600 border-indigo-200',
  },
  responded: {
    label: 'Responded',
    icon: <CheckCircle2 className="w-3.5 h-3.5" />,
    color: 'bg-emerald-100 text-emerald-600 border-emerald-200',
  },
};

function formatTimestamp(ts: string) {
  return `${formatDateTimeInIST(ts)} IST`;
}

export function ActivityTimeline({ events }: ActivityTimelineProps) {
  if (!events || events.length === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-slate-200 px-5 py-4">
      <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">
        Activity
      </h3>
      <ol className="relative border-l border-slate-200 ml-2 space-y-4">
        {events.map((evt, idx) => {
          const meta = ACTION_META[evt.action] ?? {
            label: evt.action,
            icon: null,
            color: 'bg-slate-100 text-slate-600 border-slate-200',
          };
          return (
            <li key={`${evt.action}-${idx}`} className="ml-4">
              <span
                className={`absolute -left-[11px] flex items-center justify-center w-5 h-5 rounded-full border ${meta.color}`}
              >
                {meta.icon}
              </span>
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-sm font-semibold text-slate-700">
                  {meta.label}
                </span>
                <span className="text-xs text-slate-500">
                  by {evt.actorName}
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-0.5">
                {formatTimestamp(evt.timestamp)}
              </p>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
